import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { PeriodType } from '../../types/prompt/PeriodType';
import { Nullable } from 'primereact/ts-helpers';
import { ChatType } from '../../types/monitor/ChatType';
import { getDefaultRange } from '../../utils/utils';
import { FirmData } from '../../models/monitor/FirmData';
import { useParams, useSearchParams } from 'react-router-dom';
import useMonitorPeriodAndChatType from '../../hooks/filters/UseMonitorPeriodAndChatType';
import { useAccount } from '../account/AccountContexts';
import { DateRange } from '../../models/monitor/DateRange';
import useDateRange from '../../hooks/filters/UseDateRange';
import { StandardPeriodType } from '../../types/filters/StandardPeriodType';
import { chatTypeOptions } from '../../helpers/MonitorTypeHelpers';

interface FiltersContextType {
  monitorPeriod: PeriodType | null;
  setMonitorPeriod: Dispatch<SetStateAction<PeriodType | null>>;
  selectedFirm: FirmData | null;
  setSelectedFirm: Dispatch<SetStateAction<FirmData | null>>;
  range: Nullable<(Date | null)[]>;
  setRange: Dispatch<SetStateAction<Nullable<(Date | null)[]>>>;
  selectedChatType: ChatType | null;
  setSelectedChatType: Dispatch<SetStateAction<ChatType | null>>;
  selectedChatTypes: ChatType[];
  setSelectedChatTypes: Dispatch<SetStateAction<ChatType[]>>;
  domain: string | null;
  setDomain: Dispatch<SetStateAction<string | null>>;
  dateRange: DateRange | null;
  selectedPeriod: StandardPeriodType | null;
  setSelectedPeriod: Dispatch<SetStateAction<StandardPeriodType | null>>;
}

export const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

const parseModelsParam = (
  modelsParam: string | null,
  chat: string | undefined,
  availableTypes: ChatType[]
): ChatType[] => {
  if (modelsParam) {
    const models = modelsParam.split(',').map((m) => m.toUpperCase() as ChatType);
    const valid = models.filter((m) => availableTypes.includes(m));
    return valid.length > 0 ? valid : availableTypes;
  }

  if (chat && chat.toUpperCase() !== 'ALL') {
    const chatUpper = chat.toUpperCase() as ChatType;
    if (availableTypes.includes(chatUpper)) {
      return [chatUpper];
    }
  }

  return availableTypes;
};

const deriveEffectiveChatType = (types: ChatType[], allAvailable: ChatType[]): ChatType => {
  if (types.length === 1) return types[0];
  if (types.length >= allAvailable.length) return 'ALL';
  return 'ALL';
};

export const FiltersContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [monitorPeriod, setMonitorPeriod] = useState<PeriodType | null>(null);
  const [selectedFirm, setSelectedFirm] = useState<FirmData | null>(null);
  const [range, setRange] = useState<Nullable<(Date | null)[]>>(getDefaultRange());
  const [selectedChatType, setSelectedChatType] = useState<ChatType | null>(null);
  const [selectedChatTypes, setSelectedChatTypes] = useState<ChatType[]>([]);
  const [domain, setDomain] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<StandardPeriodType | null>(null);

  const { type, period, chat, projectId } = useParams();
  const [searchParams] = useSearchParams();

  const { accountResponse } = useAccount();
  const { monitorPeriod: monitorPeriodContext, selectedChatType: selectedChatTypeContext } = useMonitorPeriodAndChatType(period, chat, accountResponse);

  const availableChatTypes = useMemo(() => {
    if (!accountResponse) return [];
    return chatTypeOptions(accountResponse.features)
      .filter((opt) => opt.type !== 'ALL' && !opt.separator)
      .map((opt) => opt.type);
  }, [accountResponse]);

  useEffect(() => {
    setMonitorPeriod(monitorPeriodContext);

    if (availableChatTypes.length === 0) {
      const modelsParam = searchParams.get('models');
      if (!modelsParam) {
        setSelectedChatType(selectedChatTypeContext);
      }
      return;
    }

    const modelsParam = searchParams.get('models');
    const parsedTypes = parseModelsParam(modelsParam, chat, availableChatTypes);
    setSelectedChatTypes(parsedTypes);

    const effective = deriveEffectiveChatType(parsedTypes, availableChatTypes);
    setSelectedChatType(effective);
  }, [monitorPeriodContext, selectedChatTypeContext, chat, searchParams, availableChatTypes]);

  useDateRange(projectId ? Number(projectId) : 0, setDateRange, setRange, setSelectedPeriod);

  return (
    <FiltersContext.Provider
      value={{
        monitorPeriod,
        selectedFirm,
        range,
        selectedChatType,
        selectedChatTypes,
        domain,
        dateRange,
        selectedPeriod,
        setMonitorPeriod,
        setSelectedFirm,
        setRange,
        setSelectedChatType,
        setSelectedChatTypes,
        setDomain,
        setSelectedPeriod,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw Error('useFilters must be used with an FiltersContextProvider');
  }

  return context;
};
